import { useState, useEffect, FormEvent } from "react";

const services = [
  {
    icon: "ri-pencil-ruler-2-line",
    title: "System Design & Engineering",
    desc: "NICET-certified design of code-compliant fire alarm systems tailored to your facility, occupancy, and jurisdiction. Full stamped drawings and permit packages.",
  },
  {
    icon: "ri-tools-line",
    title: "Turn-Key Installation",
    desc: "In-house crews handle everything from conduit and wire pull to head-end programming and AHJ acceptance testing—one point of accountability, one clean handoff.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Inspection & Testing",
    desc: "NFPA 72 annual, semi-annual, and quarterly inspections with digital reports, deficiency photos, and automated compliance reminders.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Preventive Maintenance",
    desc: "Scheduled service contracts that keep panels, notification appliances, and initiating devices running reliably year-round.",
  },
  {
    icon: "ri-alarm-warning-line",
    title: "Monitoring & Central Station",
    desc: "UL-Listed 24/7 monitoring with AES mesh network. Fast communication via phone for emergency signals and text for non-emergency signals.",
  },
  {
    icon: "ri-flashlight-line",
    title: "24/7 Emergency Response",
    desc: "On-call technicians respond around the clock for trouble signals, false alarms, water flow events, and full-system outages.",
  },
];

const brands = [
  { name: "Kidde Commercial", tag: "Detection & Suppression", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/fa944f5f-9e37-4d17-b2ab-2c0b0e9aa23b_compressed_kiddecommercialproductlogo.webp" },
  { name: "Mircom", tag: "Networked Fire Alarm", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/9831dd4c-9422-4fdf-8359-34f52d5ce79e_compressed_Unknown.webp" },
  { name: "Autocall", tag: "Addressable Systems", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/4ece3a11-6a11-49c1-9eb0-b07d63bf22e8_compressed_Unknown.webp" },
  { name: "Potter", tag: "Sprinkler Monitoring", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/78a01fd4-15b7-4d2f-87e2-8bc06ab27176_compressed_Unknown-2.webp" },
  { name: "Hochiki", tag: "Analog Addressable", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/e2324557-d3df-49e6-9a08-5c64913cc23b_compressed_Unknown-2.webp" },
  { name: "Silent Knight", tag: "Honeywell Series", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/da60fda6-e901-45e6-a34c-3bb924e166ca_compressed_Unknown-3.webp" },
  { name: "Fire-Lite", tag: "Commercial FACPs", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/3c755958-2423-4e98-b9a7-66d8f35c77d0_compressed_Unknown-4.webp" },
  { name: "Secutron", tag: "Life Safety Panels", logo: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/42209388-dec2-4f69-bede-a8b0f2d56a30_compressed_Unknown-3.webp" },
];

const caseStudies = [
  {
    title: "Downtown Tower Retrofit",
    client: "5-story office building high-rise, Las Vegas ",
    scope: "Retrofit of the existing system while the building stayed fully occupied and functional.",
    result: "A code compliant system that is user friendly for the staff to use for monthly fire drills.",
    img: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/1f367adc-510e-49dd-a7d1-59a608e67729_IMG_1144.heic",
    tags: ["Retrofit", "High-Rise", "Kidde"],
  },
  {
    title: "Regional Distribution Warehouse",
    client: "620,000 sq ft logistics campus, North Las Vegas",
    scope: "Design-build addressable system integrated with ESFR sprinkler flow and tamper monitoring across five buildings and a rack storage mezzanine.",
    result: "Delivered 3 weeks ahead of schedule. Zero deficiencies on semi-annual inspection two years running.",
    img: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/1b3ec787-0097-45bb-aa3e-2ed855ebb8f1_compressed_IMG_3545.webp",
    tags: ["Design-Build", "Industrial", "Potter"],
  },
  {
    title: "K-8 Charter School Modernization",
    client: "Conversion from a church building to a charter school, Clark County",
    scope: "District-wide standardization on a single addressable platform with mass-notification voice evacuation and classroom strobe upgrades to current ADA candela.",
    result: "A code compliant emergency voice and evacuation system across the whole campus.",
    img: "https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/e7b68868-26f2-48e9-adef-673b08a7d8d4_IMG_7333.heic",
    tags: ["Voice Evac", "Education", "Mircom"],
  },
];

const testimonials = [
  {
    name: "Frank G.",
    role: "Property Manager",
    quote: "Best customer service in town! I switched over to Ace Fire after having issues with the monitoring of the sprinkler system. Everyone has been outstanding from the salesman that came out to the office staff keeping me up to date with the process.",
  },
  {
    name: "Lucas Brandise",
    role: "General Contractor, Brandise Construction",
    quote: "Over the last few years, Ace Fire has become a company that we rely on and trust. Their team is attentive, diligent, and always service focused, with an attitude that makes you feel like the project is important. A great team that we are happy to work with.",
  },
  {
    name: "Joe Spencer",
    role: "Property Manager",
    quote: "As a property manager, I am always seeking the highest quality of services to provide the best experience and results for my tenants. Ace Fire Systems provides the exact type of customer service and experience I look for.",
  },
];

const stats = [
  { k: "22+", v: "Years in Business" },
  { k: "1,800+", v: "Systems Serviced" },
  { k: "24/7", v: "Emergency Response" },
  { k: "8", v: "Manufacturer Dealerships" },
];

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [activeCase, setActiveCase] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    const honey = (fd.get("website_alt") as string | null)?.trim();
    if (honey) {
      setStatus("success");
      formEl.reset();
      return;
    }
    fd.delete("website_alt");
    setStatus("sending");
    setFormError("");
    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, v.toString()));
      const res = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { /* non-json */ }
      const msg = parsed?.message || text;
if (res.ok && parsed?.ok === true) {
        setStatus("success");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(msg && typeof msg === "string" ? msg : "Something went wrong. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setFormError("Network error. Please try again.");
    }
  };

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#capabilities", label: "Capabilities" },
    { href: "#brands", label: "Brands" },
    { href: "#work", label: "Case Studies" },
    { href: "#testimonials", label: "Clients" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <div className="bg-background-50 text-foreground-900 font-sans">
      {/* NAV */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-background-50/95 backdrop-blur border-b border-background-200" : "bg-foreground-950/60 backdrop-blur md:bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-8 py-3 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/466fbc05-fa71-4890-9c77-e70701fc2568_compressed_IMG-5326.jpeg.webp"
              alt="Ace Fire Systems Logo"
              className={`w-10 h-10 rounded-md object-cover transition ${scrolled ? "" : "bg-white"}`}
            />
            <div className={`leading-tight ${scrolled ? "text-foreground-950" : "text-background-50"}`}>
              <div className="font-heading font-extrabold text-base tracking-tight">ACE FIRE SYSTEMS</div>
              <div className={`text-[10px] uppercase tracking-[0.18em] ${scrolled ? "text-foreground-600" : "text-background-200"}`}>Life Safety • Nevada</div>
            </div>
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className={`text-sm font-medium cursor-pointer transition ${
                  scrolled ? "text-foreground-700 hover:text-primary-600" : "text-background-100 hover:text-background-50"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+17023842932" className={`text-sm font-semibold flex items-center gap-2 whitespace-nowrap cursor-pointer ${scrolled ? "text-foreground-900" : "text-background-50"}`}>
              <i className="ri-phone-fill"></i> (702) 384-2932
            </a>
            <a href="#contact" className="px-4 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition">
              Request Quote
            </a>
          </div>
          <button className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer" onClick={() => setMenuOpen((v) => !v)}>
            <i className={`ri-${menuOpen ? "close" : "menu"}-line text-2xl ${scrolled ? "text-foreground-900" : "text-background-50"}`}></i>
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4 flex flex-col gap-3">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="text-sm font-medium text-foreground-800 cursor-pointer">{l.label}</a>
            ))}
            <a href="#contact" className="mt-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold text-center whitespace-nowrap cursor-pointer">Request Quote</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="relative w-full min-h-[520px] md:h-[720px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Dramatic%20cinematic%20wide%20shot%20of%20a%20modern%20commercial%20building%20interior%20corridor%20at%20night%20with%20a%20softly%20glowing%20red%20fire%20alarm%20pull%20station%20and%20strobe%20on%20a%20clean%20white%20wall%2C%20subtle%20warm%20accent%20lighting%2C%20polished%20concrete%20floors%2C%20professional%20architectural%20photography%2C%20moody%20editorial%20atmosphere%20with%20simple%20uncluttered%20background%20and%20deep%20shadows&width=1800&height=1100&seq=hero-firealarm-01&orientation=landscape"
          alt="Modern commercial fire alarm system"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/75 via-foreground-950/60 to-foreground-950/80"></div>
        <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-8 text-center pt-28 md:pt-0">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20 text-background-100 text-[10px] md:text-xs font-medium mb-6 backdrop-blur">
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
            Licensed • Bonded • Insured — NV C-41 Fire Protection
          </div>
          <h1 className="font-heading font-extrabold text-background-50 text-3xl md:text-6xl lg:text-7xl leading-snug md:leading-[1.05] tracking-tight">
            Fire Alarm Systems,<br />Engineered to Protect What Matters.
          </h1>
          <p className="mt-6 text-background-100 text-base md:text-lg max-w-2xl mx-auto">
            Turn-key design, installation, inspection, and 24/7 emergency response for commercial, industrial, and multi-family properties across Nevada.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#contact" className="px-6 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center gap-2 whitespace-nowrap cursor-pointer transition">
              Request a Site Assessment <i className="ri-arrow-right-line"></i>
            </a>
            <a href="#services" className="px-6 py-3.5 rounded-md bg-background-50/10 hover:bg-background-50/20 border border-background-50/30 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition backdrop-blur">
              Explore Capabilities
            </a>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {stats.map((s) => (
              <div key={s.v} className="text-center">
                <div className="font-heading font-extrabold text-background-50 text-3xl md:text-4xl">{s.k}</div>
                <div className="text-background-200 text-xs md:text-sm mt-1 uppercase tracking-wider">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-background-100 border-y border-background-200 py-6">
        <div className="max-w-6xl mx-auto px-4 md:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-foreground-600 text-xs md:text-sm">
          <span className="flex items-center gap-2"><i className="ri-shield-star-line text-primary-500"></i> NICET Certified Technicians</span>
          <span className="flex items-center gap-2"><i className="ri-award-line text-primary-500"></i> NFPA 72 Compliant</span>
          <span className="flex items-center gap-2"><i className="ri-verified-badge-line text-primary-500"></i> UL-Listed Central Station</span>
          <span className="flex items-center gap-2"><i className="ri-file-shield-2-line text-primary-500"></i> AHJ-Approved Submittals</span>
          <span className="flex items-center gap-2"><i className="ri-24-hours-line text-primary-500"></i> 24/7 On-Call</span>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24 md:py-28 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="text-primary-600 text-sm font-semibold uppercase tracking-[0.2em] mb-3">What We Do</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl leading-tight text-foreground-950">
              One partner for the entire life of your fire alarm system.
            </h2>
            <p className="mt-5 text-foreground-600 text-base md:text-lg">
              From the first code review to a routine semi-annual inspection ten years later, Ace Fire owns the whole scope—so nothing gets lost between engineers, installers, and service crews.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <div key={s.title} className="group p-7 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition">
                <div className={`w-12 h-12 flex items-center justify-center rounded-md ${i === 1 ? "bg-primary-500 text-background-50" : "bg-primary-50 text-primary-600"} mb-5`}>
                  <i className={`${s.icon} text-2xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950">{s.title}</h3>
                <p className="mt-3 text-foreground-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="py-24 md:py-28 bg-background-100 border-y border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img
              src="https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/db78880f-e23f-4377-baac-5117f3d3453e_compressed_SVTzN15p4cDGEMkRZSAKlA.webp"
              alt="Kidde Commercial Evolve Series fire alarm system"
              className="w-full h-[560px] object-contain bg-background-50 rounded-lg"
            />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-background-50 border border-background-200 rounded-lg p-5 w-64">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                  <i className="ri-check-double-line text-lg"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-600">Single-Pass AHJ</div>
                  <div className="font-heading font-bold text-foreground-950">98% Acceptance</div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="text-primary-600 text-sm font-semibold uppercase tracking-[0.2em] mb-3">Turn-Key Capabilities</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-foreground-950 leading-tight">
              We handle every step so you never have to coordinate five vendors.
            </h2>
            <div className="mt-8 space-y-5">
              {[
                { t: "Code review & AHJ coordination", d: "IBC, IFC, NFPA 72, and local amendment analysis before a single wire is pulled." },
                { t: "Stamped engineered drawings", d: "Battery calcs, voltage drop, riser diagrams, and floor plans submitted permit-ready." },
                { t: "In-house installation crews", d: "No sub-labor. Our licensed technicians own the workmanship from rough-in to close-out." },
                { t: "Programming, commissioning & training", d: "Full acceptance testing with the AHJ plus on-site training for your facilities team." },
                { t: "Ongoing service agreements", d: "Flexible PM plans with digital reports, deficiency photos, and proactive parts stocking." },
              ].map((row, i) => (
                <div key={row.t} className="flex gap-4">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading font-bold text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="font-heading font-bold text-foreground-950">{row.t}</div>
                    <div className="text-sm text-foreground-600 mt-1">{row.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section id="brands" className="py-24 md:py-28 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-xl">
              <div className="text-primary-600 text-sm font-semibold uppercase tracking-[0.2em] mb-3">Authorized Dealer Network</div>
              <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-foreground-950 leading-tight">
                Factory-trained on the platforms that matter.
              </h2>
            </div>
            <p className="text-foreground-600 text-sm md:text-base max-w-md">
              Because we're authorized on eight major manufacturers, we specify the right system for your building—not just the one we happen to stock.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {brands.map((b) => (
              <div key={b.name} className="p-6 rounded-lg bg-background-100 border border-background-200 hover:bg-background-50 hover:border-primary-300 transition flex flex-col justify-between h-32">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500/10 text-primary-600">
                  {b.logo ? (
                    <img src={b.logo} alt={`${b.name} logo`} className="w-8 h-8 object-contain rounded-md" />
                  ) : (
                    <i className="ri-fire-line"></i>
                  )}
                </div>
                <div>
                  <div className="font-heading font-bold text-foreground-950 text-base">{b.name}</div>
                  <div className="text-foreground-500 text-xs mt-1">{b.tag}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 p-6 rounded-lg bg-secondary-900 text-background-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                <i className="ri-information-line text-lg"></i>
              </div>
              <div>
                <div className="font-heading font-bold text-background-50">Not sure which platform fits your building?</div>
                <div className="text-sm text-background-200 mt-1">Our design team will spec a system based on occupancy, budget, and long-term serviceability—not brand preference.</div>
              </div>
            </div>
            <a href="#contact" className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition">
              Talk to a Designer
            </a>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section id="work" className="py-24 md:py-28 bg-background-100 border-y border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-primary-600 text-sm font-semibold uppercase tracking-[0.2em] mb-3">Case Studies</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-foreground-950 leading-tight">
              Selected projects. Real deficiencies closed. Real inspections passed.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {caseStudies.map((c, i) => (
                <button
                  key={c.title}
                  onClick={() => setActiveCase(i)}
                  className={`w-full text-left p-5 rounded-lg border transition cursor-pointer ${
                    activeCase === i
                      ? "bg-background-50 border-primary-400"
                      : "bg-background-50/60 border-background-200 hover:border-background-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {c.tags.map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800">{t}</span>
                    ))}
                  </div>
                  <div className={`font-heading font-bold text-base ${activeCase === i ? "text-primary-700" : "text-foreground-950"}`}>{c.title}</div>
                  <div className="text-xs text-foreground-600 mt-1">{c.client}</div>
                </button>
              ))}
            </div>
            <div className="lg:col-span-3">
              <div className="rounded-lg overflow-hidden bg-background-50 border border-background-200">
                <img src={caseStudies[activeCase].img} alt={caseStudies[activeCase].title} className="w-full h-72 md:h-96 object-cover object-center" />
                <div className="p-7">
                  <h3 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">{caseStudies[activeCase].title}</h3>
                  <p className="mt-4 text-sm text-foreground-700 leading-relaxed"><span className="font-semibold text-foreground-900">Scope: </span>{caseStudies[activeCase].scope}</p>
                  <div className="mt-5 p-4 rounded-md bg-primary-50 border-l-4 border-primary-500">
                    <div className="text-xs uppercase tracking-wider text-primary-700 font-bold mb-1">Result</div>
                    <div className="text-sm text-foreground-800">{caseStudies[activeCase].result}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-24 md:py-28 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-primary-600 text-sm font-semibold uppercase tracking-[0.2em] mb-3">Client Voices</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-foreground-950 leading-tight">
              Facility teams and GCs who count on us.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={t.name} className={`p-7 rounded-lg border ${i === 1 ? "bg-secondary-900 border-secondary-900 text-background-100" : "bg-background-100 border-background-200"}`}>
                <i className={`ri-double-quotes-l text-3xl ${i === 1 ? "text-accent-500" : "text-primary-500"}`}></i>
                <p className={`mt-4 text-sm leading-relaxed ${i === 1 ? "text-background-100" : "text-foreground-700"}`}>{t.quote}</p>
                <div className="mt-6 pt-5 border-t border-background-200/20">
                  <div className={`font-heading font-bold text-sm ${i === 1 ? "text-background-50" : "text-foreground-950"}`}>{t.name}</div>
                  <div className={`text-xs mt-0.5 ${i === 1 ? "text-background-200" : "text-foreground-500"}`}>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + CONTACT */}
      <section id="contact" className="py-24 md:py-28 bg-background-100 border-t border-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="text-primary-600 text-sm font-semibold uppercase tracking-[0.2em] mb-3">Get In Touch</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-foreground-950 leading-tight">
              Ready for a site walk?
            </h2>
            <p className="mt-5 text-foreground-600 text-base">
              Tell us about your project—new build, TI, inspection contract, or emergency service. A senior estimator will get back within one business day.
            </p>
            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50"><i className="ri-phone-fill text-lg"></i></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-foreground-500">24/7 Dispatch</div>
                  <div className="font-heading font-bold text-foreground-950">(702) 384-2932</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50"><i className="ri-mail-fill text-lg"></i></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-foreground-500">Email</div>
                  <div className="font-heading font-bold text-foreground-950">info@acefirenv.com</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50"><i className="ri-map-pin-fill text-lg"></i></div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-foreground-500">Office</div>
                  <div className="font-heading font-bold text-foreground-950">2620 Western Ave, Las Vegas, NV 89109</div>
                </div>
              </div>
            </div>
          </div>
          <form
            onSubmit={handleSubmit}
            className="p-8 rounded-lg bg-background-50 border border-background-200"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-foreground-700 uppercase tracking-wider">Full Name</label>
                <input required name="name" type="text" maxLength={80} className="mt-2 w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-500" placeholder="Jane Doe" />
              </div>
              <div>
                <label className="text-xs font-semibold text-foreground-700 uppercase tracking-wider">Company</label>
                <input name="company" type="text" maxLength={80} className="mt-2 w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-500" placeholder="Acme Property Group" />
              </div>
              <div>
                <label className="text-xs font-semibold text-foreground-700 uppercase tracking-wider">Email</label>
                <input required name="email" type="email" maxLength={120} className="mt-2 w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-500" placeholder="you@company.com" />
              </div>
              <div>
                <label className="text-xs font-semibold text-foreground-700 uppercase tracking-wider">Phone</label>
                <input name="phone" type="tel" maxLength={30} className="mt-2 w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-500" placeholder="(702) 555-0000" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-foreground-700 uppercase tracking-wider">Service Needed</label>
                <select name="service" className="mt-2 w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-500 cursor-pointer">
                  <option>New Installation / Design-Build</option>
                  <option>Inspection & Testing</option>
                  <option>Service Contract</option>
                  <option>Retrofit / Upgrade</option>
                  <option>Emergency Service</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-foreground-700 uppercase tracking-wider">Project Details</label>
                <textarea required name="message" rows={4} maxLength={500} className="mt-2 w-full px-3.5 py-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-500 resize-none" placeholder="Tell us about the building, timeline, and scope..." />
              </div>
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
            </div>
            <button
              type="submit"
              disabled={status === "sending"}
              className="mt-6 w-full px-6 py-3.5 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-semibold flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer transition"
            >
              {status === "sending" ? "Sending..." : <>Send Request <i className="ri-send-plane-fill"></i></>}
            </button>
            {status === "success" && (
              <div className="mt-4 p-3 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm flex items-center gap-2">
                <i className="ri-checkbox-circle-line"></i> Thanks—your request is in. We'll be in touch within one business day.
              </div>
            )}
            {status === "error" && (
              <div className="mt-4 p-3 rounded-md bg-accent-100 border border-accent-300 text-accent-900 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line"></i> {formError || "Something went wrong."}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-secondary-900 text-background-200">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <img
                src="https://storage.helloreaddy.io/project_files/a9da11ea-cf6e-497a-b5ee-c8289e5bde3d/2f3a9656-ab52-4226-a0b0-a21bf1e60b7d_compressed_IMG-5315.webp"
                alt="Ace Fire Systems Logo"
                className="w-10 h-10 rounded-md object-cover"
              />
              <div className="font-heading font-extrabold text-background-50 text-lg">ACE FIRE SYSTEMS, LLC</div>
            </div>
            <p className="mt-5 text-sm text-background-200 max-w-md leading-relaxed">
              A Nevada-based life safety contractor delivering turn-key fire alarm design, installation, and service since 1993. Licensed C-41 Fire Protection • NV #0036746.
            </p>
          </div>
          <div>
            <div className="text-background-50 font-heading font-bold text-sm mb-4">Services</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#services" className="hover:text-background-50 cursor-pointer">Design & Engineering</a></li>
              <li><a href="#services" className="hover:text-background-50 cursor-pointer">Installation</a></li>
              <li><a href="#services" className="hover:text-background-50 cursor-pointer">Inspection & Testing</a></li>
              <li><a href="#services" className="hover:text-background-50 cursor-pointer">24/7 Emergency</a></li>
            </ul>
          </div>
          <div>
            <div className="text-background-50 font-heading font-bold text-sm mb-4">Contact</div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2"><i className="ri-phone-line"></i> (702) 384-2932</li>
              <li className="flex items-center gap-2"><i className="ri-mail-line"></i> info@acefirenv.com</li>
              <li className="flex items-start gap-2"><i className="ri-map-pin-line mt-0.5"></i> 2620 Western Ave.<br />Las Vegas, NV 89109</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-background-50/10">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-background-300">
            <div>© {new Date().getFullYear()} Ace Fire Systems, LLC. All rights reserved.</div>
            <div className="flex items-center gap-5">
              <a href="#" className="hover:text-background-50 cursor-pointer">Privacy</a>
              <a href="#" className="hover:text-background-50 cursor-pointer">Terms</a>
              <a href="#" className="hover:text-background-50 cursor-pointer">Licensing</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
